import React, { memo, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import useLoadingPhrases from 'shards/Copilot/hooks/useLoadingPhrases';

import { ANIMATION_DURATION, DONE_CENTER_DELAY } from './constants';
import styles from './styles';

const useStyles = createUseStyles(styles, 'CopilotBarLoading');

/**
 * CopilotBarLoading Component
 *
 * Displays animated loading phrases that slide from bottom to top. The entering phrase
 * starts below the viewport, slides up into view, and pushes the previous phrase up
 * and out of the viewport. No scaling or rotation.
 * Uses the useLoadingPhrases hook to cycle through entertaining messages during loading.
 *
 * @param {node} [doneContent='Done!'] - Content to display when loading completes
 * @param {node} [initialLoadingPhrase] - Optional phrase to always show first when loading starts
 * @param {boolean} isLoading - Whether the loading state is active
 * @param {Array<node>} [loadingPhrases] - Custom array of phrases to cycle through during loading
 */
const CopilotBarLoading = memo(
    ({ doneContent, initialLoadingPhrase, isLoading, loadingPhrases }) => {
        const classes = useStyles();
        const { t } = useWeb5Context();

        // Get current phrase from the hook
        const currentPhrase = useLoadingPhrases({
            initialPhrase: initialLoadingPhrase,
            isLoading,
            phrases: loadingPhrases,
        });

        // Determine what to display
        const displayText = isLoading
            ? currentPhrase
            : (doneContent ?? t('label_copilot_bar_default_done'));
        const isDone = !isLoading;

        // Track 3 phrases: exiting (top), current (center), entering (bottom)
        // Initialize with a phantom phrase so the first real phrase immediately transitions to center
        const [phrases, setPhrases] = useState([
            { id: 'initial-phantom', isDone: false, text: '' },
        ]);
        const animationCleanupTimerRef = useRef(null);
        const doneRotationTimerRef = useRef(null);
        const isFirstPhraseRef = useRef(true);

        useEffect(() => {
            // Reset first phrase flag when loading stops
            if (!isLoading) {
                isFirstPhraseRef.current = true;
            }

            if (displayText) {
                // If this is the first loading phrase, add it with a phantom to immediately push to center
                if (isLoading && isFirstPhraseRef.current && displayText !== '') {
                    isFirstPhraseRef.current = false;
                    setPhrases((prev) => [
                        ...prev,
                        {
                            id: `${displayText}-${Date.now()}`,
                            isDone,
                            text: displayText,
                        },
                        // Add phantom immediately so first phrase starts at center position
                        { id: 'first-phantom', isDone: false, text: '' },
                    ]);
                } else {
                    setPhrases((prev) => {
                        // Filter out any phantom phrases before adding new real phrase
                        const withoutPhantoms = prev.filter(
                            (phrase) => !phrase.id.includes('phantom'),
                        );
                        const newPhrases = [
                            ...withoutPhantoms,
                            {
                                id: `${displayText}-${Date.now()}`,
                                isDone,
                                text: displayText,
                            },
                        ];

                        return newPhrases;
                    });
                }

                // Clean up oldest phrases after CSS animation completes
                if (animationCleanupTimerRef.current) {
                    clearTimeout(animationCleanupTimerRef.current);
                }

                animationCleanupTimerRef.current = setTimeout(() => {
                    setPhrases((prev) => (prev.length > 3 ? prev.slice(-3) : prev));
                }, ANIMATION_DURATION);

                // If this is the "Done!" message, rotate it to center position after brief display
                if (isDone) {
                    if (doneRotationTimerRef.current) {
                        clearTimeout(doneRotationTimerRef.current);
                    }

                    doneRotationTimerRef.current = setTimeout(() => {
                        // Add a phantom phrase to push "Done!" from bottom to center
                        setPhrases((prev) => [...prev, { id: 'phantom', isDone: false, text: '' }]);
                    }, DONE_CENTER_DELAY);
                }
            }

            return () => {
                if (animationCleanupTimerRef.current) {
                    clearTimeout(animationCleanupTimerRef.current);
                }
                if (doneRotationTimerRef.current) {
                    clearTimeout(doneRotationTimerRef.current);
                }
            };
        }, [displayText, isDone, isLoading]);

        // Slide-up effect: new phrase enters from bottom, pushes current to top, then exits
        // Bottom (off-screen) → Center (visible) → Top (off-screen)
        const getSlots = () => {
            const len = phrases.length;

            // Always maintain 3 slots, but map phrases to positions based on count
            const topPhrase = len >= 3 ? phrases[len - 3] : null;
            const centerPhrase = len >= 2 ? phrases[len - 2] : null;
            const bottomPhrase = len >= 1 ? phrases[len - 1] : null;

            return [
                {
                    phrase: topPhrase,
                    position: classes.phraseTop,
                    key: topPhrase?.id || 'top-empty',
                },
                {
                    phrase: centerPhrase,
                    position: classes.phraseCenter,
                    key: centerPhrase?.id || 'center-empty',
                },
                {
                    phrase: bottomPhrase,
                    position: classes.phraseBottom,
                    key: bottomPhrase?.id || 'bottom-empty',
                },
            ];
        };

        const slots = getSlots();

        return (
            <div className={classes.container}>
                {slots.map((slot) => {
                    const hasContent = slot.phrase?.text;
                    return (
                        <div
                            key={slot.key}
                            className={classnames(classes.phrase, slot.position, {
                                [classes.phraseDone]: slot.phrase?.isDone,
                                [classes.phraseEmpty]: !hasContent,
                            })}
                        >
                            {hasContent ? slot.phrase.text : '\u00A0'}
                        </div>
                    );
                })}
            </div>
        );
    },
);

CopilotBarLoading.displayName = 'CopilotBarLoading';

CopilotBarLoading.propTypes = {
    doneContent: PropTypes.node,
    initialLoadingPhrase: PropTypes.node,
    isLoading: PropTypes.bool.isRequired,
    loadingPhrases: PropTypes.arrayOf(PropTypes.node),
};

export default CopilotBarLoading;
